import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  Image,
  Dimensions,
  Animated,
  Platform,
} from 'react-native';
import resolveAssetSource from 'react-native/Libraries/Image/resolveAssetSource';
import { Link, Redirect, useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { posts } from '@/content/blog/posts';
import {
  Users,
  Heart,
  Calculator,
  ArrowRight,
  Check,
  X,
  Menu,
} from 'lucide-react-native';
import { Ionicons } from '@expo/vector-icons';
import ContactForm from '@/components/ContactForm';

const resolveWebAssetUri = (source: any): string | null => {
  if (Platform.OS !== 'web') return null;
  const resolved = resolveAssetSource(source);
  if (resolved && typeof resolved === 'object' && 'uri' in resolved) {
    const uri = (resolved as any).uri;
    return typeof uri === 'string' ? uri : null;
  }
  return null;
};

const useDimensions = () => {
  const [dimensions, setDimensions] = useState(() => {
    const { width, height } = Dimensions.get('window');
    return { width, height };
  });

  React.useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setDimensions({ width: window.width, height: window.height });
    });

    return () => subscription?.remove();
  }, []);

  return dimensions;
};

const FAQItem = ({ question, answer }: { question: string; answer: string }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const animatedHeight = useRef(new Animated.Value(0)).current;
  const { colors } = useTheme();

  const toggleExpanded = () => {
    const toValue = isExpanded ? 0 : 1;
    setIsExpanded(!isExpanded);
    Animated.timing(animatedHeight, {
      toValue,
      duration: 300,
      useNativeDriver: false,
    }).start();
  };

  const faqStyles = StyleSheet.create({
    faqItem: {
      borderWidth: 1,
      borderRadius: 12,
      overflow: 'hidden',
      marginBottom: 16,
    },
    faqQuestion: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 20,
    },
    faqQuestionText: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      flex: 1,
      marginRight: 16,
    },
    faqAnswer: {
      paddingHorizontal: 20,
      paddingBottom: 20,
    },
    faqAnswerText: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      lineHeight: 22,
    },
  });

  return (
    <View style={[faqStyles.faqItem, { borderColor: colors.border }]}>
      <TouchableOpacity
        style={faqStyles.faqQuestion}
        onPress={toggleExpanded}
        activeOpacity={0.7}
      >
        <Text style={[faqStyles.faqQuestionText, { color: colors.text }]}>{question}</Text>
        <Ionicons
          name={isExpanded ? 'chevron-up' : 'chevron-down'}
          size={20}
          color={colors.text}
        />
      </TouchableOpacity>

      {isExpanded && (
        <Animated.View style={[faqStyles.faqAnswer, { opacity: animatedHeight }]}>
          <Text style={[faqStyles.faqAnswerText, { color: colors.textSecondary }]}>{answer}</Text>
        </Animated.View>
      )}
    </View>
  );
};

export default function LandingPage() {
  const { user } = useAuth();
  const router = useRouter();
  const { colors } = useTheme();
  const { width } = useDimensions();
  const scrollViewRef = useRef<ScrollView>(null);
  const sectionCoords = useRef<{ [key: string]: number }>({});

  // Função para capturar a posição Y de cada seção
  const handleLayout = (sectionId: string, event: any) => {
    const layout = event.nativeEvent.layout;
    sectionCoords.current[sectionId] = layout.y;
  };

  // Estado para controlar o menu mobile
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Breakpoints responsivos
  const isWeb = width > 1024;
  const isTablet = width > 768 && width <= 1024;
  const isMobile = width <= 768;

  // IMPORTANTE: Todos os hooks devem vir ANTES de qualquer return condicional
  // para evitar "Rendered fewer hooks than expected"
  const latestPosts = React.useMemo(() => {
    return [...posts].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 3);
  }, []);

  // Se o usuário estiver logado, redireciona para as tabs
  if (user) {
    return <Redirect href="/(tabs)" />;
  }

  const handleLogin = () => {
    router.push('/(auth)/login');
  };

  const handleRegister = () => {
    router.push('/(auth)/register');
  };

  const handleTermos = () => {
    router.push('/termos-de-uso');
  };

  const handlePrivacidade = () => {
    router.push('/politica-privacidade');
  };

  const handleSobre = () => {
    router.push('/sobre');
  };

  const handleAssinar = () => {
    router.push('/assinatura');
  };
  const handleBlog = () => {
    router.push('/blog');
  };
  const handleContato = () => {
    router.push('/contato');
  };

  // Função para scroll suave para seções
  const scrollToSection = (sectionId: string) => {
    // Offset opcional para compensar cabeçalho fixo ou dar respiro (ex: 20px)
    const OFFSET = 20;
    const y = sectionCoords.current[sectionId];

    if (scrollViewRef.current && y !== undefined) {
      scrollViewRef.current.scrollTo({
        y: Math.max(0, y - OFFSET),
        animated: true,
      });
    }
  };

  const faqData = [
    {
      question: "O aplicativo é realmente gratuito para usar?",
      answer: "Sim! O plano gratuito permite manter 1 racha ativo simultaneamente e usar todas as funcionalidades essenciais. Oferecemos o plano Premium Anual para quem deseja criar rachas ilimitados, acessar recursos avançados como o Vínculo de Bolso, e ter uma experiência sem anúncios."
    },
    {
      question: "O que é o 'Vínculo de Bolso'?",
      answer: "É um recurso premium que permite agrupar pessoas que dividem as mesmas finanças, como um casal ou família. As despesas são divididas por pessoa, mas na hora de acertar as contas, o saldo do grupo é consolidado, simplificando os pagamentos."
    },
    {
      question: "Como funciona o 'Balanço Simplificado'?",
      answer: "É um recurso inteligente disponível para todos. Em vez de mostrar múltiplas transferências, nosso algoritmo calcula o menor número de pagamentos necessários para que todos no racha quitem suas dívidas, economizando tempo e transferências."
    },
    {
      question: "Meus dados estão seguros?",
      answer: "Sim. A segurança dos seus dados é nossa prioridade. Utilizamos o Firebase, a plataforma segura do Google, para armazenar todas as informações, e o Stripe, líder mundial em pagamentos, para processar as assinaturas de forma criptografada."
    }
  ];

  // Estilos responsivos
  const styles = StyleSheet.create({
    container: {
      flex: 1,
    },
    scrollContainer: {
      flexGrow: 1,
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: isWeb ? 32 : isTablet ? 24 : 16,
      paddingVertical: isWeb ? 20 : isTablet ? 16 : 12,
      borderBottomWidth: 1,
    },
    logoContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      flex: 1,
    },
    logoImage: {
      height: 40,
      width: 40,
    },
    logoText: {
      fontSize: 18,
      fontFamily: 'Inter-Bold',
    },
    navigation: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: isWeb ? 40 : isTablet ? 32 : 16,
      flex: 2,
      justifyContent: 'center',
    },
    navLink: {
      fontSize: isWeb ? 16 : isTablet ? 14 : 12,
      fontFamily: 'Inter-Medium',
      paddingVertical: isWeb ? 10 : isTablet ? 8 : 6,
      paddingHorizontal: isWeb ? 8 : isTablet ? 4 : 8,
    },
    rightSection: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      flex: 1,
      justifyContent: 'flex-end',
    },
    mobileMenuButton: {
      padding: 8,
    },
    loginButton: {
      paddingHorizontal: isWeb ? 24 : isTablet ? 20 : 16,
      paddingVertical: isWeb ? 12 : isTablet ? 10 : 8,
      borderRadius: 20,
      alignItems: 'center',
    },
    loginButtonText: {
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
    },
    mobileMenu: {
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderBottomWidth: 1,
    },
    mobileMenuItem: {
      paddingVertical: 12,
      paddingHorizontal: 8,
    },
    mobileMenuText: {
      fontSize: 14,
      fontFamily: 'Inter-Medium',
    },

    // Hero Section
    heroSection: {
      paddingHorizontal: isWeb ? 32 : isTablet ? 24 : 16,
      paddingVertical: isWeb ? 80 : isTablet ? 60 : 40,
      alignItems: 'center',
    },
    heroTitle: {
      fontSize: isWeb ? 40 : isTablet ? 32 : 24,
      fontFamily: 'Inter-Bold',
      textAlign: 'center',
      lineHeight: isWeb ? 48 : isTablet ? 40 : 32,
      marginBottom: isWeb ? 20 : 16,
    },
    heroSubtitle: {
      fontSize: isWeb ? 20 : isTablet ? 18 : 16,
      fontFamily: 'Inter-Regular',
      textAlign: 'center',
      lineHeight: isWeb ? 28 : isTablet ? 26 : 24,
      marginBottom: isWeb ? 40 : isTablet ? 32 : 24,
      maxWidth: isWeb ? 700 : isTablet ? 600 : width - 32,
    },
    heroImage: {
      width: Math.min(width - 48, 400),
      height: Math.min(width - 48, 400) * 0.75,
      borderRadius: 16,
      marginBottom: 32,
    },
    heroImageText: {
      fontSize: 16,
      marginTop: 12,
      textAlign: 'center',
    },
    ctaButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: 32,
      paddingVertical: 16,
      borderRadius: 25,
      elevation: 4,
      ...(Platform.OS === 'web' ? {
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)',
      } : {
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
      }),
    },
    ctaButtonText: {
      fontSize: 18,
      fontFamily: 'Inter-SemiBold',
    },

    // Blog Highlights Section
    blogSection: {
      paddingHorizontal: isWeb ? 32 : isTablet ? 24 : 16,
      paddingVertical: isWeb ? 64 : isTablet ? 48 : 32,
      backgroundColor: colors.surface + '10',
    },
    blogTitle: {
      fontSize: isWeb ? 28 : isTablet ? 24 : 20,
      fontFamily: 'Inter-Bold',
      textAlign: 'center',
      marginBottom: isWeb ? 32 : isTablet ? 28 : 20,
    },
    blogGrid: {
      flexDirection: isWeb ? 'row' : 'column',
      flexWrap: isWeb ? 'wrap' as any : 'nowrap' as any,
      gap: 20,
      justifyContent: 'center',
      alignItems: 'stretch',
    },
    blogCard: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 12,
      overflow: 'hidden',
      backgroundColor: colors.surface,
      width: isWeb ? '31%' : '100%',
    },
    blogImage: {
      width: '100%',
      height: 'auto' as any,
      aspectRatio: 3000 / 1150,
      objectFit: 'cover' as any,
    },
    blogCardContent: {
      paddingHorizontal: 16,
      paddingVertical: 16,
      gap: 8,
    },
    blogCardTitle: {
      fontSize: 18,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
    },
    blogCardExcerpt: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    blogCardButton: {
      marginTop: 12,
      paddingVertical: 12,
      borderRadius: 10,
      alignItems: 'center',
      backgroundColor: colors.primary,
    },
    blogCardButtonText: {
      color: '#FFFFFF',
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
    },
    blogViewAll: {
      marginTop: 24,
      alignItems: 'center',
    },
    blogViewAllText: {
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
      color: colors.primary,
    },

    // Features Section
    featuresSection: {
      paddingHorizontal: isWeb ? 32 : isTablet ? 24 : 16,
      paddingVertical: isWeb ? 80 : isTablet ? 60 : 40,
    },
    sectionTitle: {
      fontSize: isWeb ? 32 : isTablet ? 28 : 22,
      fontFamily: 'Inter-Bold',
      textAlign: 'center',
      marginBottom: isWeb ? 56 : isTablet ? 48 : 32,
      lineHeight: isWeb ? 40 : isTablet ? 36 : 28,
    },
    featuresGrid: {
      gap: 24,
    },
    featureCard: {
      alignItems: 'center',
      padding: isWeb ? 32 : isTablet ? 24 : 16,
      borderRadius: 16,
      borderWidth: 1,
      marginBottom: isWeb ? 40 : isTablet ? 32 : 20,
      elevation: 2,
      ...(Platform.OS === 'web' ? {
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
      } : {
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      }),
    },
    featureIcon: {
      width: 80,
      height: 80,
      borderRadius: 40,
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: 16,
    },
    featureTitle: {
      fontSize: 20,
      fontFamily: 'Inter-SemiBold',
      marginBottom: 8,
      textAlign: 'center',
    },
    featureDescription: {
      fontSize: 16,
      fontFamily: 'Inter-Regular',
      lineHeight: 24,
      textAlign: 'center',
    },

    // How It Works Section
    howItWorksSection: {
      paddingHorizontal: isWeb ? 32 : isTablet ? 24 : 16,
      paddingVertical: isWeb ? 80 : isTablet ? 60 : 40,
      backgroundColor: colors.surface + '80',
    },
    stepsContainer: {
      gap: isWeb ? 32 : isTablet ? 24 : 20,
    },
    stepCard: {
      padding: isWeb ? 32 : isTablet ? 24 : 20,
      borderRadius: 16,
      borderWidth: 1,
      alignItems: 'center',
      elevation: 2,
      ...(Platform.OS === 'web' ? {
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
      } : {
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
      }),
    },
    stepNumber: {
      width: 50,
      height: 50,
      borderRadius: 25,
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: 16,
    },
    stepNumberText: {
      fontSize: 20,
      fontFamily: 'Inter-Bold',
    },
    stepTitle: {
      fontSize: 20,
      fontFamily: 'Inter-SemiBold',
      marginBottom: 12,
      textAlign: 'center',
    },
    stepDescription: {
      fontSize: 16,
      fontFamily: 'Inter-Regular',
      lineHeight: 24,
      textAlign: 'center',
      maxWidth: isWeb ? 600 : '100%',
    },

    // Plans Section
    plansSection: {
      paddingHorizontal: isWeb ? 32 : isTablet ? 24 : 16,
      paddingVertical: isWeb ? 80 : isTablet ? 60 : 40,
      overflow: 'visible',
    },
    plansGrid: {
      flexDirection: isWeb ? 'row' : 'column',
      gap: 24,
      justifyContent: 'center',
      alignItems: isWeb ? 'flex-start' : 'center',
      overflow: 'visible',
    },
    planCard: {
      width: isWeb ? 300 : '100%',
      maxWidth: 350,
      minHeight: 380,
      padding: 24,
      borderRadius: 16,
      borderWidth: 2,
      position: 'relative',
      overflow: 'visible',
    },
    planCardHighlight: {
      transform: isWeb ? [{ scale: 1.05 }] : [],
    },
    planBadge: {
      position: 'absolute',
      top: -15,
      left: 20,
      right: 20,
      backgroundColor: colors.warning,
      paddingVertical: 8,
      paddingHorizontal: 16,
      borderRadius: 20,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      borderWidth: 1,
      borderColor: colors.warning,
      zIndex: 999999,
      elevation: 50,
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 6,
      },
      shadowOpacity: 0.4,
      shadowRadius: 12,
    },
    planBadgeText: {
      fontSize: 12,
      fontFamily: 'Inter-SemiBold',
    },
    planTitle: {
      fontSize: 24,
      fontFamily: 'Inter-Bold',
      textAlign: 'center',
      marginBottom: 16,
      marginTop: 8,
    },
    planPriceContainer: {
      flexDirection: 'row',
      alignItems: 'baseline',
      justifyContent: 'center',
      marginBottom: 24,
      gap: 4,
    },
    planPrice: {
      fontSize: 32,
      fontFamily: 'Inter-Bold',
    },
    planPriceSubtext: {
      fontSize: 16,
      fontFamily: 'Inter-Regular',
    },
    planFeatures: {
      gap: 12,
      marginBottom: 24,
    },
    planFeature: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    planFeatureText: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      flex: 1,
    },
    planButton: {
      paddingVertical: 16,
      borderRadius: 12,
      alignItems: 'center',
    },
    planButtonText: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
    },

    // FAQ Section
    faqSection: {
      paddingHorizontal: isWeb ? 32 : isTablet ? 24 : 16,
      paddingVertical: isWeb ? 80 : isTablet ? 60 : 40,
    },
    faqContainer: {
      gap: 16,
      maxWidth: 800,
      alignSelf: 'center',
      width: '100%',
    },

    // Footer
    footer: {
      paddingHorizontal: isWeb ? 32 : isTablet ? 24 : 16,
      paddingVertical: isWeb ? 48 : isTablet ? 40 : 32,
      borderTopWidth: 1,
    },
    footerContent: {
      alignItems: 'center',
      gap: 24,
    },
    footerLogo: {
      alignItems: 'center',
    },
    footerLogoText: {
      fontSize: 18,
      fontFamily: 'Inter-Bold',
    },
    footerLinks: {
      flexDirection: isMobile ? 'column' : 'row',
      gap: isWeb ? 40 : isTablet ? 32 : 16,
      alignItems: 'center',
    },
    footerLink: {
      fontSize: 14,
      fontFamily: 'Inter-Medium',
    },
    footerCopyright: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      textAlign: 'center',
    },
  });

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      <ScrollView
        ref={scrollViewRef}
        style={styles.container}
        contentContainerStyle={styles.scrollContainer}
        showsVerticalScrollIndicator={false}
      >
        {/* Navigation Header */}
        <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
          {/* Logo e Título - Alinhados à esquerda */}
          <View style={styles.logoContainer}>
            <Image
              source={require('@/public/icon.png')}
              style={styles.logoImage}
              resizeMode="contain"
            />
            <Text style={[styles.logoText, { color: colors.text }]}>Põe no Racha</Text>
          </View>

          {/* Menu para Web - Sempre visível */}
          {!isMobile && (
            <View style={styles.navigation}>
              <TouchableOpacity onPress={() => scrollToSection('funcionalidades')} activeOpacity={0.7}>
                <Text style={[styles.navLink, { color: colors.textSecondary }]}>Funcionalidades</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => scrollToSection('como-funciona')} activeOpacity={0.7}>
                <Text style={[styles.navLink, { color: colors.textSecondary }]}>Como Funciona</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => scrollToSection('planos')} activeOpacity={0.7}>
                <Text style={[styles.navLink, { color: colors.textSecondary }]}>Planos</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => scrollToSection('faq')} activeOpacity={0.7}>
                <Text style={[styles.navLink, { color: colors.textSecondary }]}>FAQ</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={handleBlog} activeOpacity={0.7}>
                <Text style={[styles.navLink, { color: colors.textSecondary }]}>Blog</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* Botões à direita */}
          <View style={styles.rightSection}>
            {/* Menu hambúrguer para Mobile */}
            {isMobile && (
              <TouchableOpacity
                style={styles.mobileMenuButton}
                onPress={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                activeOpacity={0.7}
              >
                <Menu size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            )}

            {/* Botão Entrar */}
            <TouchableOpacity
              style={[styles.loginButton, { backgroundColor: colors.primary }]}
              onPress={handleLogin}
              activeOpacity={0.7}
            >
              <Text style={[styles.loginButtonText, { color: '#FFFFFF' }]}>Entrar</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Menu Mobile Dropdown */}
        {isMobile && isMobileMenuOpen && (
          <View style={[styles.mobileMenu, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
            <TouchableOpacity
              style={styles.mobileMenuItem}
              onPress={() => {
                scrollToSection('funcionalidades');
                setIsMobileMenuOpen(false);
              }}
              activeOpacity={0.7}
            >
              <Text style={[styles.mobileMenuText, { color: colors.textSecondary }]}>Funcionalidades</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.mobileMenuItem}
              onPress={() => {
                scrollToSection('como-funciona');
                setIsMobileMenuOpen(false);
              }}
              activeOpacity={0.7}
            >
              <Text style={[styles.mobileMenuText, { color: colors.textSecondary }]}>Como Funciona</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.mobileMenuItem}
              onPress={() => {
                scrollToSection('planos');
                setIsMobileMenuOpen(false);
              }}
              activeOpacity={0.7}
            >
              <Text style={[styles.mobileMenuText, { color: colors.textSecondary }]}>Planos</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.mobileMenuItem}
              onPress={() => {
                scrollToSection('faq');
                setIsMobileMenuOpen(false);
              }}
              activeOpacity={0.7}
            >
              <Text style={[styles.mobileMenuText, { color: colors.textSecondary }]}>FAQ</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.mobileMenuItem}
              onPress={() => {
                handleBlog();
                setIsMobileMenuOpen(false);
              }}
              activeOpacity={0.7}
            >
              <Text style={[styles.mobileMenuText, { color: colors.textSecondary }]}>Blog</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Hero Section */}
        <View style={styles.heroSection} onLayout={(e) => handleLayout('inicio', e)}>
          {Platform.OS === 'web' ? (
            <>
              <h1
                style={{
                  ...(styles.heroTitle as any),
                  color: colors.text,
                  margin: 0,
                  lineHeight: `${(styles.heroTitle as any).lineHeight}px`,
                }}
              >
                Divida contas com seus amigos de forma simples e justa
              </h1>
              <p
                style={{
                  ...(styles.heroSubtitle as any),
                  color: colors.textSecondary,
                  margin: 0,
                  lineHeight: `${(styles.heroSubtitle as any).lineHeight}px`,
                }}
              >
                Automatize cálculos complexos e elimine conflitos financeiros. Perfeito para viagens, festas e despesas do dia a dia.
              </p>
            </>
          ) : (
            <>
              <Text style={[styles.heroTitle, { color: colors.text }]}>
                Divida contas com seus amigos de forma simples e justa
              </Text>
              <Text style={[styles.heroSubtitle, { color: colors.textSecondary }]}>
                Automatize cálculos complexos e elimine conflitos financeiros. Perfeito para viagens, festas e despesas do dia a dia.
              </Text>
            </>
          )}

          {Platform.OS === 'web' ? (
            <img
              src={resolveWebAssetUri(require('@/assets/images/imagem1.webp')) ?? undefined}
              alt="Ilustração do app Põe no Racha"
              fetchPriority="high"
              loading="eager"
              decoding="async"
              style={{ ...(styles.heroImage as any), objectFit: 'cover' }}
            />
          ) : (
            <Image
              source={require('@/assets/images/imagem1.png')}
              style={styles.heroImage}
              resizeMode="cover"
            />
          )}

          <TouchableOpacity
            style={[styles.ctaButton, { backgroundColor: colors.primary }]}
            onPress={handleRegister}
            activeOpacity={0.8}
          >
            <Text style={[styles.ctaButtonText, { color: '#FFFFFF' }]}>Começar Agora</Text>
            <ArrowRight size={20} color="#FFFFFF" />
          </TouchableOpacity>
        </View>

        {Platform.OS === 'web' ? (
          <View style={styles.blogSection}>
            <h2
              style={{
                ...(styles.blogTitle as any),
                color: colors.text,
                margin: 0,
                lineHeight: (styles.blogTitle as any).lineHeight
                  ? `${(styles.blogTitle as any).lineHeight}px`
                  : undefined,
              }}
            >
              Dicas de Finanças e Convivência
            </h2>
            <View style={styles.blogGrid}>
              {latestPosts.map((post) => (
                <View key={post.slug} style={styles.blogCard}>
                  {post.coverImage ? (
                    <img
                      src={post.coverImage}
                      alt={`Capa do artigo: ${post.title}`}
                      loading="lazy"
                      decoding="async"
                      style={styles.blogImage as any}
                    />
                  ) : null}
                  <View style={styles.blogCardContent}>
                    <Text style={styles.blogCardTitle}>{post.title}</Text>
                    <Text style={styles.blogCardExcerpt}>{post.excerpt}</Text>
                    <Link href={`/blog/${post.slug}`} asChild>
                      <TouchableOpacity style={styles.blogCardButton} activeOpacity={0.8}>
                        <Text style={styles.blogCardButtonText}>Ler mais</Text>
                      </TouchableOpacity>
                    </Link>
                  </View>
                </View>
              ))}
            </View>
            <View style={styles.blogViewAll}>
              <Link href="/blog" asChild>
                <TouchableOpacity activeOpacity={0.8}>
                  <Text style={styles.blogViewAllText}>Ver todos os artigos →</Text>
                </TouchableOpacity>
              </Link>
            </View>
          </View>
        ) : null}

        {/* Features Section */}
        <View style={styles.featuresSection} onLayout={(e) => handleLayout('funcionalidades', e)}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>Funcionalidades</Text>

          <View style={styles.featuresGrid}>
            <View style={[styles.featureCard, { backgroundColor: colors.surface, borderColor: colors.border }]}>
              <View style={[styles.featureIcon, { backgroundColor: colors.primary + '20' }]}>
                <Users size={40} color={colors.primary} />
              </View>
              <Text style={[styles.featureTitle, { color: colors.text }]}>Grupos Ilimitados</Text>
              <Text style={[styles.featureDescription, { color: colors.textSecondary }]}>
                Crie grupos para viagens, festas, despesas compartilhadas e qualquer evento. Cada grupo mantém seu histórico independente.
              </Text>
            </View>

            <View style={[styles.featureCard, { backgroundColor: colors.surface, borderColor: colors.border }]}>
              <View style={[styles.featureIcon, { backgroundColor: colors.success + '20' }]}>
                <Calculator size={40} color={colors.success} />
              </View>
              <Text style={[styles.featureTitle, { color: colors.text }]}>Cálculo Automático</Text>
              <Text style={[styles.featureDescription, { color: colors.textSecondary }]}>
                Algoritmo inteligente que calcula automaticamente quanto cada pessoa deve pagar ou receber, sem erros de planilha.
              </Text>
            </View>

            <View style={[styles.featureCard, { backgroundColor: colors.surface, borderColor: colors.border }]}>
              <View style={[styles.featureIcon, { backgroundColor: colors.warning + '20' }]}>
                <Heart size={40} color={colors.warning} />
              </View>
              <Text style={[styles.featureTitle, { color: colors.text }]}>Fácil de Usar</Text>
              <Text style={[styles.featureDescription, { color: colors.textSecondary }]}>
                Interface intuitiva para todas as idades. Adicione despesas em poucos toques, anexe fotos e mantenha o histórico organizado.
              </Text>
            </View>
          </View>
        </View>

        {/* How It Works Section */}
        <View style={styles.howItWorksSection} onLayout={(e) => handleLayout('como-funciona', e)}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>Como Funciona</Text>

          {/* Imagem ilustrativa */}
          {Platform.OS === 'web' ? (
            <img
              src={resolveWebAssetUri(require('@/assets/images/imagem2.webp')) ?? undefined}
              alt="Demonstração de uso do app Põe no Racha"
              loading="lazy"
              decoding="async"
              style={{
                width: Math.min(width - 48, 500),
                height: Math.min(width - 48, 500) * 0.6,
                borderRadius: 16,
                alignSelf: 'center',
                marginBottom: 32,
                objectFit: 'cover',
                display: 'block',
                marginLeft: 'auto',
                marginRight: 'auto',
              } as any}
            />
          ) : (
            <Image
              source={require('@/assets/images/imagem2.png')}
              style={{
                width: Math.min(width - 48, 500),
                height: Math.min(width - 48, 500) * 0.6,
                borderRadius: 16,
                alignSelf: 'center',
                marginBottom: 32,
              }}
              resizeMode="cover"
            />
          )}

          <View style={styles.stepsContainer}>
            <View style={[styles.stepCard, { backgroundColor: colors.surface, borderColor: colors.border }]}>
              <View style={[styles.stepNumber, { backgroundColor: colors.primary }]}>
                <Text style={[styles.stepNumberText, { color: '#FFFFFF' }]}>1</Text>
              </View>
              <Text style={[styles.stepTitle, { color: colors.text }]}>Crie seu Grupo</Text>
              <Text style={[styles.stepDescription, { color: colors.textSecondary }]}>
                Crie um grupo com nome descritivo. Todos acompanham as despesas em tempo real.
              </Text>
            </View>

            <View style={[styles.stepCard, { backgroundColor: colors.surface, borderColor: colors.border }]}>
              <View style={[styles.stepNumber, { backgroundColor: colors.success }]}>
                <Text style={[styles.stepNumberText, { color: '#FFFFFF' }]}>2</Text>
              </View>
              <Text style={[styles.stepTitle, { color: colors.text }]}>Adicione as Despesas</Text>
              <Text style={[styles.stepDescription, { color: colors.textSecondary }]}>
                Registre gastos rapidamente informando valor, descrição e participantes.
              </Text>
            </View>

            <View style={[styles.stepCard, { backgroundColor: colors.surface, borderColor: colors.border }]}>
              <View style={[styles.stepNumber, { backgroundColor: colors.warning }]}>
                <Text style={[styles.stepNumberText, { color: '#FFFFFF' }]}>3</Text>
              </View>
              <Text style={[styles.stepTitle, { color: colors.text }]}>Acerte as Contas</Text>
              <Text style={[styles.stepDescription, { color: colors.textSecondary }]}>
                O algoritmo calcula automaticamente quem deve para quem, otimizando pagamentos. Visão clara de débitos e créditos com sugestões de PIX.
              </Text>
            </View>
          </View>
        </View>

        {/* Plans Section */}
        <View style={styles.plansSection} onLayout={(e) => handleLayout('planos', e)}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>Planos</Text>

          <View style={styles.plansGrid}>
            {/* Plano Gratuito */}
            <View style={[styles.planCard, { backgroundColor: colors.surface, borderColor: colors.border }]}>
              <Text style={[styles.planTitle, { color: colors.text }]}>Free</Text>
              <View style={styles.planPriceContainer}>
                <Text style={[styles.planPrice, { color: colors.text }]}>R$ 0</Text>
              </View>
              <View style={styles.planFeatures}>
                <View style={styles.planFeature}>
                  <Check size={16} color={colors.success} />
                  <Text style={[styles.planFeatureText, { color: colors.textSecondary }]}>Apenas 1 racha simultâneo</Text>
                </View>
                <View style={styles.planFeature}>
                  <Check size={16} color={colors.success} />
                  <Text style={[styles.planFeatureText, { color: colors.textSecondary }]}>Balanço simplificado</Text>
                </View>
                <View style={styles.planFeature}>
                  <X size={16} color={colors.error || '#ef4444'} />
                  <Text style={[styles.planFeatureText, { color: colors.textSecondary }]}>Sem vínculo de bolso</Text>
                </View>
                <View style={styles.planFeature}>
                  <Check size={16} color={colors.textSecondary} />
                  <Text style={[styles.planFeatureText, { color: colors.textSecondary }]}>Com propagandas</Text>
                </View>
                <View style={styles.planFeature}>
                  <Check size={16} color={colors.textSecondary} />
                  <Text style={[styles.planFeatureText, { color: colors.textSecondary }]}>Suporte padrão</Text>
                </View>
              </View>
              <TouchableOpacity
                style={[styles.planButton, { backgroundColor: colors.border }]}
                onPress={handleRegister}
                activeOpacity={0.8}
              >
                <Text style={[styles.planButtonText, { color: colors.text }]}>Começar Grátis</Text>
              </TouchableOpacity>
            </View>

            {/* Plano Anual */}
            <View style={[styles.planCard, styles.planCardHighlight, { backgroundColor: colors.surface, borderColor: colors.primary }]}>
              <View style={{ position: 'absolute', top: -16, left: 0, right: 0, alignItems: 'center', zIndex: 10 }}>
                <Image source={require('@/assets/images/crown.png')} style={{ width: 32, height: 32, backgroundColor: 'transparent', borderRadius: 16 }} resizeMode="contain" />
              </View>
              <Text style={[styles.planTitle, { color: colors.text }]}>Premium Anual</Text>
              <View style={styles.planPriceContainer}>
                <Text style={[styles.planPrice, { color: colors.primary }]}>R$ 9,90/ano</Text>
              </View>
              <View style={styles.planFeatures}>
                <View style={styles.planFeature}>
                  <Check size={16} color={colors.success} />
                  <Text style={[styles.planFeatureText, { color: colors.textSecondary }]}>Rachas ilimitados</Text>
                </View>
                <View style={styles.planFeature}>
                  <Check size={16} color={colors.success} />
                  <Text style={[styles.planFeatureText, { color: colors.textSecondary }]}>Balanço simplificado</Text>
                </View>
                <View style={styles.planFeature}>
                  <Check size={16} color={colors.success} />
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.planFeatureText, { color: colors.textSecondary }]}>Vínculo de bolso liberado</Text>
                    <Text style={{ fontSize: 10, color: colors.textSecondary, marginTop: 2 }}>
                      Agrupe casais/famílias como uma só entidade no balanço
                    </Text>
                  </View>
                </View>
                <View style={styles.planFeature}>
                  <Check size={16} color={colors.success} />
                  <Text style={[styles.planFeatureText, { color: colors.textSecondary }]}>Sem propagandas</Text>
                </View>
                <View style={styles.planFeature}>
                  <Check size={16} color={colors.success} />
                  <Text style={[styles.planFeatureText, { color: colors.textSecondary }]}>Suporte prioritário</Text>
                </View>
              </View>
              <TouchableOpacity
                style={[styles.planButton, { backgroundColor: colors.primary }]}
                onPress={handleAssinar}
                activeOpacity={0.8}
              >
                <Text style={[styles.planButtonText, { color: '#FFFFFF' }]}>Assinar Agora</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>


        {/* FAQ Section */}
        <View style={styles.faqSection} onLayout={(e) => handleLayout('faq', e)}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>Perguntas Frequentes</Text>

          <View style={styles.faqContainer}>
            {faqData.map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </View>

          {/* Contact Form Section */}
          <View style={{ paddingHorizontal: isWeb ? 32 : isTablet ? 24 : 16, paddingBottom: 80 }}>
            <ContactForm startCollapsed={true} />
          </View>
        </View>

        {/* Footer */}
        <View style={[styles.footer, { backgroundColor: colors.surface, borderTopColor: colors.border }]}>
          <View style={styles.footerContent}>
            <View style={styles.footerLogo}>
              <Text style={[styles.footerLogoText, { color: colors.text }]}>Põe no Racha</Text>
            </View>

            <View style={styles.footerLinks}>
              <TouchableOpacity onPress={handleBlog} activeOpacity={0.7}>
                <Text style={[styles.footerLink, { color: colors.primary }]}>Blog</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={handleSobre} activeOpacity={0.7}>
                <Text style={[styles.footerLink, { color: colors.primary }]}>Sobre Nós</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={handleTermos} activeOpacity={0.7}>
                <Text style={[styles.footerLink, { color: colors.primary }]}>Termos de Uso</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={handlePrivacidade} activeOpacity={0.7}>
                <Text style={[styles.footerLink, { color: colors.primary }]}>Política de Privacidade</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={handleContato} activeOpacity={0.7}>
                <Text style={[styles.footerLink, { color: colors.primary }]}>Contato</Text>
              </TouchableOpacity>
            </View>

            <Text style={[styles.footerCopyright, { color: colors.textSecondary }]}>
              © 2025 Põe no Racha. Todos os direitos reservados.
            </Text>
          </View>
        </View>
      </ScrollView >
    </SafeAreaView >
  );
}
